import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, AlertCircle, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { getSyncStatus, syncTracker } from "@/lib/utils/sync-tracker";

interface SyncStatusProps {
  className?: string;
}

export function SyncStatus({ className = "" }: SyncStatusProps) {
  const [status, setStatus] = useState(getSyncStatus());
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setStatus(getSyncStatus());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleManualSync = async () => {
    setIsRefreshing(true);
    try {
      await syncTracker.syncPendingChanges();
    } finally {
      setIsRefreshing(false);
    }
  };

  const getStatusIcon = () => {
    if (status.isSyncing) {
      return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
    }
    if (status.pendingChanges > 0) {
      return <AlertCircle className="h-4 w-4 text-orange-500" />;
    }
    return <CheckCircle className="h-4 w-4 text-green-500" />;
  };

  const getStatusText = () => {
    if (status.isSyncing) {
      return "Syncing...";
    }
    if (status.pendingChanges > 0) {
      return `${status.pendingChanges} pending`;
    }
    return "Up to date";
  };

  const getStatusColor = () => {
    if (status.isSyncing) {
      return "bg-blue-50 text-blue-700 border-blue-200";
    }
    if (status.pendingChanges > 0) {
      return "bg-orange-50 text-orange-700 border-orange-200";
    }
    return "bg-green-50 text-green-700 border-green-200";
  };

  return (
    <div className={`${className} flex items-center gap-2 px-2 py-1 bg-background border rounded-md`}>
      {getStatusIcon()}
      <div className="flex flex-col">
        <span className="text-xs font-medium">Sync</span>
        <Badge variant="outline" className={`${getStatusColor()} text-xs px-1 py-0 h-4`}>
          {getStatusText()}
        </Badge>
      </div>
      {status.pendingChanges > 0 && (
        <Button
          size="sm"
          variant="ghost"
          onClick={handleManualSync}
          disabled={status.isSyncing || isRefreshing}
          className="h-5 w-5 p-0"
        >
          {isRefreshing ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3" />
          )}
        </Button>
      )}
    </div>
  );
} 